import React from 'react';
import Select from 'react-select';

interface Props {
  kind: 'certification' | 'findings';
  placement: 'default' | 'custom';
  afterGroupId: number | null;
  groups: Array<{ id: number; name: string }>;
  onSetDefault: () => void;
  onSetCustom: () => void;
  onAnchorChange: (afterGroupId: number | null) => void;
}

const TITLE: Record<Props['kind'], string> = {
  certification: 'Certification & Signatures',
  findings: 'Findings Log',
};

const ANCHOR_WIDTH = 260;

const anchorStyles = {
  container: (base: any) => ({ ...base, width: ANCHOR_WIDTH, maxWidth: '100%' }),
  control: (base: any) => ({
    ...base,
    minHeight: 34,
    borderColor: '#cbd2d9',
    fontSize: 13,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#002230',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  menu: (base: any) => ({ ...base, width: ANCHOR_WIDTH, maxWidth: '100%' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any) => ({
    ...base,
    fontSize: 13,
    lineHeight: 1.35,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  }),
};

const PlacementBlock: React.FC<Props> = ({
  kind,
  placement,
  afterGroupId,
  groups,
  onSetDefault,
  onSetCustom,
  onAnchorChange,
}) => {
  const custom = placement === 'custom';

  const anchorOptions = [
    { value: '', label: 'Bottom of document' },
    ...groups.map((group) => ({
      value: String(group.id),
      label: `After: ${group.name}`,
    })),
  ];

  const selectedAnchor =
    anchorOptions.find(
      (option) => option.value === String(afterGroupId ?? ''),
    ) ?? anchorOptions[0];

  return (
    <div className={`ple-block ple-placement-block ple-placement-block--${kind}`}>
      <div className='ple-placement-block__bar'>
        <span className='ple-block__tag'>{TITLE[kind]}</span>
        <div className='ple-seg'>
          <button
            type='button'
            className={!custom ? 'is-active' : ''}
            onClick={onSetDefault}
          >
            Default
          </button>
          <button
            type='button'
            className={custom ? 'is-active' : ''}
            onClick={onSetCustom}
          >
            Customise
          </button>
        </div>
        {custom && (
          <Select
            className='ple-placement-block__anchor'
            classNamePrefix='ple-anchor'
            styles={anchorStyles}
            options={anchorOptions}
            value={selectedAnchor}
            isSearchable={false}
            menuPlacement='auto'
            maxMenuHeight={220}
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : undefined
            }
            menuPosition='fixed'
            onChange={(option: any) =>
              onAnchorChange(option && option.value ? Number(option.value) : null)
            }
          />
        )}
      </div>
      <div className='ple-placement-block__preview'>
        {kind === 'certification' ? (
          <>
            <span className='ple-line ple-line--40' />
            <span className='ple-line ple-line--60' />
            <span className='ple-sign'>Signature ___________</span>
          </>
        ) : (
          <>
            <span className='ple-line ple-line--full' />
            <span className='ple-line ple-line--full' />
            <span className='ple-line ple-line--80' />
          </>
        )}
      </div>
    </div>
  );
};

export default PlacementBlock;
